import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { transactionAPI } from '../services/api';
import { MonthlySummary as SummaryType, Transaction, BudgetProgress } from '../types';
import api from '../services/api';

const MonthlySummary: React.FC = () => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<SummaryType | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [budgetProgress, setBudgetProgress] = useState<BudgetProgress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);

  const loadMonthlySummary = async (year: number, month: number) => {
    try {
      setLoading(true);
      setError('');
      
      const [summaryData, transactionData, budgetData] = await Promise.all([
        transactionAPI.getMonthlySummary(year, month),
        transactionAPI.getMonthly(year, month),
        api.get(`/budget-progress/${year}/${month}`).then(res => res.data).catch(() => [])
      ]);
      
      setSummary(summaryData);
      setTransactions(transactionData);
      setBudgetProgress(budgetData);
    } catch (err) {
      console.error('月次データ読み込みエラー:', err);
      setError(t('common:errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return formatCurrency(amount);
  };

  const getCategoryBreakdown = (type: 'income' | 'expense') => {
    const filtered = transactions.filter(t => t.type === type);
    const breakdown = filtered.reduce((acc, transaction) => {
      const category = transaction.category || t('common:other');
      acc[category] = (acc[category] || 0) + Number(transaction.amount);
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(breakdown)
      .sort(([, a], [, b]) => b - a)
      .map(([category, amount]) => ({ category, amount }));
  };

  const getBudgetComparisonData = (type: 'income' | 'expense') => {
    const categoryBreakdown = getCategoryBreakdown(type);
    const budgetData = budgetProgress.filter(bp => bp.categoryType === type);
    
    // Create a map of actual spending by category
    const actualMap = categoryBreakdown.reduce((acc, { category, amount }) => {
      acc[category] = amount;
      return acc;
    }, {} as Record<string, number>);
    
    // Combine budget and actual data
    const combined = budgetData.map(budget => ({
      category: budget.categoryName,
      budgetAmount: budget.budgetAmount,
      actualAmount: actualMap[budget.categoryName] || 0,
      progressRate: budget.budgetAmount > 0 ? 
        Math.round((actualMap[budget.categoryName] || 0) / budget.budgetAmount * 100) : 0
    }));
    
    // Add categories that have actual amounts but no budget
    categoryBreakdown.forEach(({ category, amount }) => {
      if (!budgetData.find(b => b.categoryName === category)) {
        combined.push({
          category,
          budgetAmount: 0,
          actualAmount: amount,
          progressRate: 0
        });
      }
    });
    
    return combined.sort((a, b) => b.actualAmount - a.actualAmount);
  };

  const getProgressBarColor = (progressRate: number, budgetAmount: number) => {
    if (budgetAmount === 0) return '#6b7280'; // Gray for no budget
    if (progressRate <= 50) return '#10b981'; // Green for under 50%
    if (progressRate <= 80) return '#f59e0b'; // Orange for 50-80%
    if (progressRate <= 100) return '#ef4444'; // Red for 80-100%
    return '#dc2626'; // Dark red for over 100%
  };

  const handleMonthChange = () => {
    loadMonthlySummary(selectedYear, selectedMonth);
  };

  useEffect(() => {
    loadMonthlySummary(selectedYear, selectedMonth);
  }, []);

  const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - i);
  const months = [
    { value: 1, label: t('common:january') }, { value: 2, label: t('common:february') }, { value: 3, label: t('common:march') },
    { value: 4, label: t('common:april') }, { value: 5, label: t('common:may') }, { value: 6, label: t('common:june') },
    { value: 7, label: t('common:july') }, { value: 8, label: t('common:august') }, { value: 9, label: t('common:september') },
    { value: 10, label: t('common:october') }, { value: 11, label: t('common:november') }, { value: 12, label: t('common:december') }
  ];

  return (
    <div className="summary-container">
      <h2>{t('summary:monthlySummary')}</h2>
      
      <div className="month-selector">
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {years.map(year => (
            <option key={year} value={year}>{year}{t('common:year')}</option>
          ))}
        </select>
        
        <select 
          value={selectedMonth} 
          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
        >
          {months.map(month => (
            <option key={month.value} value={month.value}>{month.label}</option>
          ))}
        </select>
        
        <button onClick={handleMonthChange} className="btn btn-primary">
          {t('common:show')}
        </button>
      </div>

      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">{t('common:loading')}</div>
      ) : summary ? (
        <>
          <div className="summary-card">
            <h3>{t('summary:balanceSummaryFor', { year: selectedYear, month: selectedMonth })}</h3>
            <div className="summary-item">
              <span className="summary-label">{t('transaction:totalIncome')}:</span>
              <span className="summary-value income">{formatAmount(summary.income)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('transaction:totalExpense')}:</span>
              <span className="summary-value expense">{formatAmount(summary.expense)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('summary:balance')}:</span>
              <span className={`summary-value ${summary.balance >= 0 ? 'income' : 'expense'}`}>
                {formatAmount(summary.balance)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('summary:transactionCount')}:</span>
              <span className="summary-value">{summary.transactionCount}{t('common:unit')}</span>
            </div>
          </div>

          {getBudgetComparisonData('income').length > 0 && (
            <div className="summary-card">
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '20px',
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                <span style={{ fontSize: '20px' }}>💰</span>
                {t('summary:incomeBudgetComparison')}
              </h3>
              {getBudgetComparisonData('income').map(({ category, budgetAmount, actualAmount, progressRate }) => (
                <div key={category} style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                  borderRadius: '12px',
                  border: '1px solid #86efac'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: '#166534'
                    }}>{category}</span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px'
                    }}>
                      <span style={{ color: '#059669' }}>
                        💰 {formatAmount(actualAmount)}
                      </span>
                      <span style={{ color: '#6b7280' }}>vs</span>
                      <span style={{ color: budgetAmount > 0 ? '#dc2626' : '#9ca3af' }}>
                        🎯 {budgetAmount > 0 ? formatAmount(budgetAmount) : '予算なし'}
                      </span>
                    </div>
                  </div>
                  
                  {budgetAmount > 0 && (
                    <>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          width: `${Math.min(progressRate, 100)}%`,
                          height: '100%',
                          backgroundColor: getProgressBarColor(progressRate, budgetAmount),
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '13px'
                      }}>
                        <span style={{
                          color: getProgressBarColor(progressRate, budgetAmount),
                          fontWeight: '600'
                        }}>
                          {progressRate}% 達成
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          残り: {formatAmount(Math.max(0, budgetAmount - actualAmount))}
                        </span>
                      </div>
                    </>
                  )}
                  
                  {budgetAmount === 0 && (
                    <div style={{
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      padding: '8px',
                      background: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      ⚠️ 予算未設定 (達成率: 0%)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {getBudgetComparisonData('expense').length > 0 && (
            <div className="summary-card">
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '20px',
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                <span style={{ fontSize: '20px' }}>📈</span>
                {t('summary:expenseBudgetComparison')}
              </h3>
              {getBudgetComparisonData('expense').map(({ category, budgetAmount, actualAmount, progressRate }) => (
                <div key={category} style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                  borderRadius: '12px',
                  border: '1px solid #fca5a5'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: '#7f1d1d'
                    }}>{category}</span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px'
                    }}>
                      <span style={{ color: '#dc2626' }}>
                        💰 {formatAmount(actualAmount)}
                      </span>
                      <span style={{ color: '#6b7280' }}>vs</span>
                      <span style={{ color: budgetAmount > 0 ? '#059669' : '#9ca3af' }}>
                        🎯 {budgetAmount > 0 ? formatAmount(budgetAmount) : '予算なし'}
                      </span>
                    </div>
                  </div>
                  
                  {budgetAmount > 0 && (
                    <>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          width: `${Math.min(progressRate, 100)}%`,
                          height: '100%',
                          backgroundColor: getProgressBarColor(progressRate, budgetAmount),
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '13px'
                      }}>
                        <span style={{
                          color: getProgressBarColor(progressRate, budgetAmount),
                          fontWeight: '600'
                        }}>
                          {progressRate}% 消化
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          残り: {formatAmount(Math.max(0, budgetAmount - actualAmount))}
                        </span>
                      </div>
                    </>
                  )}
                  
                  {budgetAmount === 0 && (
                    <div style={{
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      padding: '8px',
                      background: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      ⚠️ 予算未設定 (消化率: 0%)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {transactions.length === 0 && (
            <div className="empty-state">
              <p>{t('summary:noTransactionData')}</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default MonthlySummary;